import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { DictionaryEntry, SuggestionPayload, SuggestionType } from '../types'
import { SUGGESTION_TYPE_LABELS } from '../types'

interface Props {
  entry: DictionaryEntry
  open: boolean
  onClose: () => void
  defaultType?: SuggestionType
}

type FormState = Omit<SuggestionPayload, 'entry_id'>
type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error'

const EMPTY_FORM: FormState = {
  submitter_name:        '',
  submitter_email:       '',
  suggestion_type:       'better_tamang',
  corrected_tamang:      '',
  corrected_nepali:      '',
  corrected_english:     '',
  suggested_romanization:'',
  dialect_note:          '',
  speaker_notes:         '',
}

export function SuggestionModal({ entry, open, onClose, defaultType }: Props) {
  const [form, setForm]         = useState<FormState>(EMPTY_FORM)
  const [status, setStatus]     = useState<SubmitStatus>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  // Reset form when opened. For romanization, pre-fill existing entry fields
  // so the admin sees full context alongside the suggested romanization.
  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY_FORM,
        suggestion_type: defaultType ?? 'better_tamang',
        ...(defaultType === 'romanization' ? {
          corrected_tamang:  entry.tamang_form_surface,
          corrected_nepali:  entry.nepali_form,
          corrected_english: entry.english_gloss_auto,
        } : {}),
      })
      setStatus('idle')
      setErrorMsg('')
    }
  }, [open, entry.entry_id, defaultType, entry.tamang_form_surface, entry.nepali_form, entry.english_gloss_auto])

  const set = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value = e.target.value
      setForm(f => {
        const next = { ...f, [field]: value }
        // Auto-switch type to romanization when that's the only field filled
        if (field === 'suggested_romanization' && value.trim()) {
          const othersFilled = next.corrected_tamang.trim() ||
            next.corrected_nepali.trim() || next.corrected_english.trim()
          if (!othersFilled) next.suggestion_type = 'romanization'
        }
        return next
      })
    }

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (status === 'submitting') return

    // At least one correction field must be filled
    const hasContent =
      form.corrected_tamang.trim() ||
      form.corrected_nepali.trim() ||
      form.corrected_english.trim() ||
      form.suggested_romanization.trim() ||
      form.dialect_note.trim() ||
      form.speaker_notes.trim()

    if (!hasContent) {
      setErrorMsg('Please fill in at least one correction or note field.')
      return
    }

    setStatus('submitting')
    setErrorMsg('')

    const payload = {
      entry_id:               entry.entry_id,
      submitter_name:         form.submitter_name.trim()         || null,
      submitter_email:        form.submitter_email.trim()        || null,
      suggestion_type:        form.suggestion_type,
      corrected_tamang:       form.corrected_tamang.trim()       || null,
      corrected_nepali:       form.corrected_nepali.trim()       || null,
      corrected_english:      form.corrected_english.trim()      || null,
      suggested_romanization: form.suggested_romanization.trim() || null,
      dialect_note:           form.dialect_note.trim()           || null,
      speaker_notes:          form.speaker_notes.trim()          || null,
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from('suggestions').insert(payload as any)

    if (error) {
      setStatus('error')
      setErrorMsg(error.message || 'Something went wrong. Please try again.')
    } else {
      setStatus('success')
    }
  }, [entry.entry_id, form, status])

  if (!open) return null

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
      aria-label="Suggest a correction"
    >
      <div className="
        bg-white w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl shadow-2xl
        max-h-[92vh] flex flex-col overflow-hidden
      ">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-stone-100">
          <div>
            <h2 className="text-lg font-semibold text-stone-800">Suggest a Correction</h2>
            <p className="text-sm text-stone-500 mt-0.5 deva">
              {entry.tamang_form_surface}
              <span className="not-italic text-stone-400 font-sans mx-2">↔</span>
              {entry.nepali_form}
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost mt-0.5"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Success state */}
        {status === 'success' ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 gap-4 text-center">
            <div className="text-4xl">🙏</div>
            <h3 className="text-xl font-semibold text-stone-800">Thank you!</h3>
            <p className="text-stone-600">
              Your suggestion has been recorded and will be reviewed.
              Community contributions like yours help improve the accuracy
              of this dictionary for Tamang speakers everywhere.
            </p>
            <button className="btn-primary mt-2" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">

            {/* Suggestion type */}
            <Field label="Type of suggestion" required>
              <select
                value={form.suggestion_type}
                onChange={set('suggestion_type')}
                className="form-select w-full rounded-lg border-stone-300 text-sm
                           focus:ring-tamang-600 focus:border-tamang-600"
              >
                {(Object.entries(SUGGESTION_TYPE_LABELS) as [SuggestionType, string][]).map(
                  ([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  )
                )}
              </select>
            </Field>

            {/* Correction fields — only ask what's relevant */}
            <div className="rounded-xl bg-stone-50 border border-stone-100 p-4 space-y-3">
              <p className="text-xs text-stone-500 font-semibold uppercase tracking-wide">
                Corrections (fill only what you know)
              </p>

              <Field label="Corrected Tamang form">
                <DevaInput
                  value={form.corrected_tamang}
                  onChange={set('corrected_tamang')}
                  placeholder="e.g. रि → री or a different word"
                />
              </Field>

              <Field label="Corrected Nepali form">
                <DevaInput
                  value={form.corrected_nepali}
                  onChange={set('corrected_nepali')}
                  placeholder="e.g. मा or a different Nepali word"
                />
              </Field>

              <Field label="Corrected English gloss">
                <input
                  type="text"
                  value={form.corrected_english}
                  onChange={set('corrected_english')}
                  placeholder="e.g. in/at/on (locative marker)"
                  className="form-input w-full rounded-lg border-stone-300 text-sm
                             focus:ring-tamang-600 focus:border-tamang-600"
                />
              </Field>

              <Field label="Romanized Tamang (Latin script)">
                <input
                  type="text"
                  value={form.suggested_romanization}
                  onChange={set('suggested_romanization')}
                  placeholder="e.g. min (for name), lha (for god)"
                  className="form-input w-full rounded-lg border-stone-300 text-sm
                             focus:ring-tamang-600 focus:border-tamang-600"
                />
              </Field>
            </div>

            {/* Dialect / notes */}
            <Field label="Dialect note">
              <DevaInput
                value={form.dialect_note}
                onChange={set('dialect_note')}
                placeholder="e.g. In Rasuwa dialect this is written as…"
                multiline
              />
            </Field>

            <Field label="Notes for reviewer">
              <textarea
                value={form.speaker_notes}
                onChange={set('speaker_notes')}
                placeholder="Any other context — why this is wrong, how to improve it, etc."
                rows={3}
                className="form-textarea w-full rounded-lg border-stone-300 text-sm
                           focus:ring-tamang-600 focus:border-tamang-600"
              />
            </Field>

            {/* Submitter info (optional) */}
            <div className="rounded-xl bg-stone-50 border border-stone-100 p-4 space-y-3">
              <p className="text-xs text-stone-500 font-semibold uppercase tracking-wide">
                About you (optional)
              </p>
              <Field label="Your name">
                <input
                  type="text"
                  value={form.submitter_name}
                  onChange={set('submitter_name')}
                  placeholder="Anonymous if left blank"
                  className="form-input w-full rounded-lg border-stone-300 text-sm
                             focus:ring-tamang-600 focus:border-tamang-600"
                />
              </Field>
              <Field label="Your email">
                <input
                  type="email"
                  value={form.submitter_email}
                  onChange={set('submitter_email')}
                  placeholder="We'll only use this to follow up on your suggestion"
                  className="form-input w-full rounded-lg border-stone-300 text-sm
                             focus:ring-tamang-600 focus:border-tamang-600"
                />
              </Field>
            </div>

            {/* Error */}
            {(status === 'error' || errorMsg) && (
              <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {errorMsg || 'Something went wrong. Please try again.'}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-2">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Submitting…' : 'Submit Suggestion'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function Field({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-stone-700">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  )
}

function DevaInput({
  value,
  onChange,
  placeholder,
  multiline,
}: {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  placeholder?: string
  multiline?: boolean
}) {
  const cls = `
    deva w-full rounded-lg border-stone-300 text-sm
    focus:ring-tamang-600 focus:border-tamang-600
  `
  return multiline ? (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={2}
      className={`form-textarea ${cls}`}
    />
  ) : (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`form-input ${cls}`}
    />
  )
}
